// Colours are read from the PrimeNG CSS variables rather than Tailwind classes: the surface-* classes
// are inert in this project, tailwindcss-primeui is not imported.
export function readCssVariable(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

export function chartTextColor(): string {
  return readCssVariable('--p-text-muted-color', '#64748b');
}

export function chartGridColor(): string {
  return readCssVariable('--p-surface-200', '#e2e8f0');
}

/** Bar chart options for the compact cards: no legend, no vertical grid, values on a single axis. */
export function compactBarOptions(formatValue: (value: number) => string) {
  const textColor = chartTextColor();

  return {
    maintainAspectRatio: false,
    plugins: {
      legend: {display: false},
      tooltip: {
        callbacks: {
          label: (context: {parsed: {y: number}}) => formatValue(context.parsed.y),
        },
      },
    },
    scales: {
      x: {ticks: {color: textColor}, grid: {display: false}, border: {display: false}},
      y: {
        beginAtZero: true,
        ticks: {color: textColor, callback: (value: number) => formatValue(value)},
        grid: {color: chartGridColor()},
        border: {display: false},
      },
    },
  };
}

/** Doughnut options: the centre is left hollow enough to host a figure. */
export function doughnutOptions() {
  return {
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: {display: false},
      tooltip: {
        callbacks: {
          label: (context: {label: string; parsed: number}) => `${context.label}: ${context.parsed}`,
        },
      },
    },
  };
}

/** Grouped vertical bars with a legend, for the two-series month comparison. */
export function groupedBarOptions(formatValue: (value: number) => string) {
  const textColor = chartTextColor();

  return {
    maintainAspectRatio: false,
    plugins: {
      legend: {position: 'top', align: 'end', labels: {color: textColor, boxWidth: 12, boxHeight: 12}},
      tooltip: {
        callbacks: {
          label: (context: {dataset: {label?: string}; parsed: {y: number}}) =>
            `${context.dataset.label}: ${formatValue(context.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {ticks: {color: textColor}, grid: {display: false}, border: {display: false}},
      y: {
        beginAtZero: true,
        ticks: {color: textColor, callback: (value: number) => formatValue(value)},
        grid: {color: chartGridColor()},
        border: {display: false},
      },
    },
  };
}

/** Horizontal bars: they absorb long labels where a doughnut needs a separate legend. */
export function horizontalBarOptions(formatValue: (value: number) => string, showLegend: boolean) {
  const textColor = chartTextColor();

  return {
    indexAxis: 'y',
    maintainAspectRatio: false,
    plugins: {
      legend: showLegend
        ? {position: 'top', align: 'end', labels: {color: textColor, boxWidth: 12, boxHeight: 12}}
        : {display: false},
      tooltip: {
        callbacks: {
          label: (context: {dataset: {label?: string}; parsed: {x: number}}) =>
            showLegend ? `${context.dataset.label}: ${formatValue(context.parsed.x)}` : formatValue(context.parsed.x),
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {color: textColor, callback: (value: number) => formatValue(value)},
        grid: {color: chartGridColor()},
        border: {display: false},
      },
      y: {ticks: {color: textColor}, grid: {display: false}, border: {display: false}},
    },
  };
}

// Categorical palette for series that have no colour of their own in the database (timesheet types).
// Hex literals rather than CSS variables: the alpha suffix below needs a known format, and a theme
// variable could resolve to oklch() where "#rrggbbaa" no longer applies.
const CATEGORICAL_PALETTE = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
  '#6366f1', // indigo
];

export function categoricalColor(index: number): string {
  return CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length];
}

/** Same hue, faded: distinguishes the previous month without spending a second colour per category. */
export function fadedCategoricalColor(index: number): string {
  return `${categoricalColor(index)}59`;
}
